import { number, object, string } from "yup";

import ICoverageArea, { ICoverageAreaInput } from "@/interfaces/coverage-area.interface";
import { CoverageAreaFormMessages } from "@/messages/coverage-area/coverage-area-form.messages";

import { CoverageAreaFormData } from "./types";

export const coverageAreaFormNames: Record<keyof CoverageAreaFormData, string> = {
  customerId: "customerId",
  startZipCode: "startZipCode",
  endZipCode: "endZipCode",
  areaDescription: "areaDescription",
  startWeight: "startWeight",
  endWeight: "endWeight",
  freightChargeOrder: "freightChargeOrder",
  freightCostCarrier: "freightCostCarrier",
  regionalDeliveryTime: "regionalDeliveryTime",
  freightType: "freightType",
  freeShippingOver: "freeShippingOver",
};

export const buildFormInitialData = (
  id: string,
  coverageArea?: ICoverageArea | null
): CoverageAreaFormData => {
  return {
    customerId: coverageArea?.customerId || id,
    startZipCode: coverageArea?.startZipCode || "",
    endZipCode: coverageArea?.endZipCode || "",

    areaDescription: coverageArea?.areaDescription || "",

    startWeight: coverageArea?.startWeight || 0,
    endWeight: coverageArea?.endWeight || 0,

    freightChargeOrder: coverageArea?.freightChargeOrder || 0,
    freightCostCarrier: coverageArea?.freightCostCarrier || 0,
    regionalDeliveryTime: coverageArea?.regionalDeliveryTime || 0,
    freightType: coverageArea?.freightType || "",
    freeShippingOver: coverageArea?.freeShippingOver ?? 0,
  };
};

export const buildCoverageAreaFormSchema = (messages: CoverageAreaFormMessages) =>
  object().shape({
    startZipCode: string().required(messages.errorRequiredCepStart),
    endZipCode: string().required(messages.errorRequiredCepEnd),
    areaDescription: string().required(messages.errorRequiredAreaDescription),

    startWeight: number()
      .typeError(messages.errorInvalidStartWeight)
      .min(0, messages.errorInvalidStartWeight)
      .required(messages.errorRequiredStartWeight),

    endWeight: number()
      .typeError(messages.errorInvalidEndWeight)
      .min(0, messages.errorInvalidEndWeight)
      .required(messages.errorRequiredEndWeight),

    freightChargeOrder: number()
      .typeError(messages.errorInvalidFreightChargeOrder)
      .min(0, messages.errorInvalidFreightChargeOrder)
      .required(messages.errorRequiredFreightChargeOrder),

    freightCostCarrier: number()
      .typeError(messages.errorInvalidFreightCostCarrier)
      .min(0, messages.errorInvalidFreightCostCarrier)
      .required(messages.errorRequiredFreightCostCarrier),

    regionalDeliveryTime: number()
      .typeError(messages.errorInvalidRegionalDeliveryTime)
      .min(0, messages.errorInvalidRegionalDeliveryTime)
      .required(messages.errorRequiredRegionalDeliveryTime),
    freightType: string().nullable().optional(),
  });

export const normalizeCoverageAreaFormData = (
  formData: CoverageAreaFormData
): ICoverageAreaInput => {
  const normalizeFreightType = (value?: string | null): string | null => {
    if (value === undefined || value === null) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;
    const lower = trimmed.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  };

  return {
    customerId: formData.customerId,
    startZipCode: formData.startZipCode,
    endZipCode: formData.endZipCode,
    areaDescription: formData.areaDescription,
    startWeight: parseFloat(Number(formData.startWeight).toFixed(3)),
    endWeight: parseFloat(Number(formData.endWeight).toFixed(3)),
    freightChargeOrder: formData.freightChargeOrder,
    freightCostCarrier: formData.freightCostCarrier,
    regionalDeliveryTime: formData.regionalDeliveryTime,
    freightType: normalizeFreightType(formData.freightType),
    freeShippingOver:
      formData.freeShippingOver === null || formData.freeShippingOver === undefined
        ? null
        : Number(formData.freeShippingOver),
  } as ICoverageAreaInput;
};
